import { GetRowActions } from '../../../components/ListV2/components/table/VirtualizedTable.types';
import { FeatureFlagEnum } from '../../../featureflag/feature-flag.enum';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAppEnvironmentStore } from '../../../stores/AppEnvironment/appEnvironment.store';
import usePaymentReleaseSettings from '../../../stores/GeneralSettings/hooks/usePaymentReleaseSettings';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useNetworkContext } from '../../offline/contexts/network/network.context';
import { useAbility } from '../../users/AbilityContext';
import { OrderActions } from '../actions/OrderActions';
import { GetActionsParams } from '../actions/types/actions.types';
import { OrderActionProps } from '../actions/types/orderActionsContext.types';
import { useOrderActionsContext } from '../contexts/OrderActions.context';
import { OrderBlockReason, OrderListWithBaseItem } from '../order.types';
import { UseOrderRowActionsParams } from './useOrderRowActions.types';

const useOrderRowActions = ({
  isArchivedOrderFilterActive = false,
  isOfflineMode = false,
}: UseOrderRowActionsParams = {}) => {
  const ability = useAbility();
  const { userHasInternet } = useNetworkContext();
  const { onClickOrderAction } = useOrderActionsContext();
  const { isEnableShareSuggestedOrder, getFeatureFlag } = useFeatureFlag();
  const appEnvironment = useAppEnvironmentStore((state) => state.appEnvironment);
  const { requirePaymentReleaseForImmediate, requirePaymentReleaseForPreOrder, refundLimitHours } =
    usePaymentReleaseSettings();
  const {
    userRole: role,
    hasIntegrations,
    integrations,
    state,
    hasOrderIntegration,
    canForceReverseOrderSync,
  } = useApplicationContext();

  const integration = integrations?.find(({ active }) => active);

  const isSystemParamsResetIntegration = getFeatureFlag(FeatureFlagEnum.RESET_INTEGRATION, false);

  const getRowActions: GetRowActions<OrderListWithBaseItem> = ({ item }) => {
    const orderActions = new OrderActions();

    const params: GetActionsParams = {
      hasSplit: !!item.orderSplitSettings?.length,
      splitted: item.orderSplitted,
      isSystemParamsResetIntegration,
      status: item.orderStatus,
      archivedOrder: item.orderArchived,
      hasIntegrations,
      orderPaymentStatus: item.orderPaymentStatus,
      synchronized: item.orderSynchronized,
      triggerIntegrationConfig: integration?.integrationTrigger,
      integrationName: integration?.name,
      canForceReverseOrderSync,
      isEnableShareSuggestedOrder,
      brand: state.brands[0],
      role,
      hasOrderIntegration,
      isEnableAcceptSuggestedOrder: getFeatureFlag(FeatureFlagEnum.ACCEPT_SUGGESTED_ORDER),
      userAbility: ability,
      lastGatewayPaymentStatus: item.lastGatewayPaymentStatus,
      lastGatewayPaymentUpdatedAt: item.lastGatewayPaymentUpdatedAt,
      refundLimitHours,
      orderType: item?.orderType,
      requirePaymentGateway: !!item.requirePaymentGateway,
      requirePaymentReleaseForImmediate,
      requirePaymentReleaseForPreOrder,
      isArchivedOrderFilterActive,
      appEnvironment,
      userHasNotInternet: isOfflineMode && !userHasInternet,
    };

    const actions = orderActions.getActions(params);

    return actions.map((action) => ({
      label: action.label,
      icon: action.icon,
      testId: action.testId,
      onClick: () => onClickOrderAction(action.value, OrderActionProps.toProps(item)),
    }));
  };

  const getIsDisabledRowAction = (item: OrderListWithBaseItem) => {
    if (item.orderBlockReason === OrderBlockReason.MASS_ACTION_STATUS_UPDATING) {
      return true;
    }

    return false;
  };

  return { getRowActions, getIsDisabledRowAction };
};

export default useOrderRowActions;
